import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LeadCapturePanel from "./LeadCapturePanel";
import LaunchBanner from "./LaunchBanner";
import ShotsCarousel from "./ShotsCarousel";
import { checkoutApi } from "../api/checkout";
import { trackBeginCheckout } from "../lib/analytics";
import { pricingApi } from "../api/pricing";
import { detectCurrency, formatFromTable } from "../lib/currency";
import { AD_SHOTS } from "../lib/shots";

// Landing page for the Instagram ads (voleor.com/ig), routed outside
// SiteLayout so it carries no nav and no footer: every link on this page is a
// way to leave it before the email field has been filled, and an ad click is
// paid for. The homepage stays the destination for organic traffic.
//
// Written for someone who has just watched a reel, not for someone who
// searched for a drum sequencer: they know what they saw, they have no idea
// what it is called, and they are on a phone where a desktop plugin cannot be
// installed. So the page names the video, then asks for the email that lets
// them resume at a computer (see LeadCapturePanel), and nothing else.
//
// Single screen on a phone: logo, headline, one line of context, email field.
// Anything that pushes the field below the fold belongs under it, which is why
// the proof points and the product shot come last.
// Mounting has to follow the screen, not only styling: LeadCapturePanel fires
// `lead_capture_shown` on mount and the capture rate is measured against that
// count, so a panel hidden behind `hidden` would inflate it on every phone.
// Hence matchMedia rather than a Tailwind class for this one decision.
function useIsDesktop() {
  const query = "(min-width: 1024px)";
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e) => setIsDesktop(e.matches);
    mql.addEventListener("change", onChange);
    setIsDesktop(mql.matches);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return isDesktop;
}

// One component, three ad destinations. /ig, /tk and /igbuy are the same pitch
// to the same person — someone who just watched a short vertical creative on a
// phone — so they share everything except the `source` that splits them apart
// in analytics. TikTok clicks carry no fbclid, so the Meta events this page can
// produce stay silent for /tk traffic on their own; no gating needed here.
//
// `buyFirst` is what /igbuy adds. The image ads quote a price in the creative
// itself, so the visitor arrives having already read a number and decided; the
// trial-first order below buries the button they came for under an email
// field. Ordering is the whole difference — the trial stays on the page,
// because those same images promise "7-day free trial, no card needed" and a
// page that drops it contradicts the click we paid for.
export default function IgLanding({ source = "ig_landing", buyFirst = false }) {
  const { t, i18n } = useTranslation("landing");
  // The buy button quotes a price, so it has to read the live one. It used to
  // be baked into the five translations, which meant a launch offer showed
  // the old price here while the rest of the site showed the new one.
  const [prices, setPrices] = useState([]);
  useEffect(() => {
    pricingApi.getPricing()
      .then((resp) => setPrices(Array.isArray(resp.data.prices) ? resp.data.prices : []))
      // A failed lookup must not break the page: the label falls back below.
      .catch(() => setPrices([]));
  }, []);
  // `?cur=` wins over region detection, same contract as the homepage: an ad
  // can be bought for one country and served in another (a UK campaign reaches
  // a Brit travelling, a VPN, an English-language placement), and the price in
  // the creative has to be the price on the page or the click is wasted.
  const location = useLocation();
  const currencyOverride = new URLSearchParams(location.search).get("cur");
  const priceRow = prices.length
    ? formatFromTable(
        prices,
        detectCurrency(i18n.language, prices.map((r) => r.currency), currencyOverride),
      )
    : null;

  const [buying, setBuying] = useState(false);
  const [buyError, setBuyError] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const isDesktop = useIsDesktop();

  async function handleBuyNow() {
    setBuyError(false);
    setBuying(true);
    try {
      trackBeginCheckout({ source, guest: true });
      const { data } = await checkoutApi.createGuestSession(null);
      // Assign rather than push: Stripe is not part of the SPA, and a back
      // button from Checkout should land here, not in a dead history entry.
      window.location.assign(data.url);
    } catch {
      setBuyError(true);
      setBuying(false);
    }
  }

  // Memoised: ShotsCarousel keys its IntersectionObserver effect off this
  // array's identity, so a fresh one per render would tear the observer down
  // and rebuild it on every keystroke in the email field.
  const shots = useMemo(
    () => [
      { id: "hero-plugin", src: "/hero-plugin.webp", alt: t("hero.imageAlt") },
      ...AD_SHOTS,
    ],
    [t],
  );

  // One button, two positions. Filled and first on /igbuy, outlined and last
  // on /ig — same handler, same event, so the funnel does not fork.
  const buyButton = (
    <>
      <button
        type="button"
        onClick={handleBuyNow}
        disabled={buying}
        className={
          buyFirst
            ? "mt-6 w-full rounded-lg bg-[#FF5500] px-6 py-3 font-body font-semibold text-[#0a0a0a] transition-colors hover:bg-[#FF7733] disabled:opacity-60"
            : "mt-3 w-full rounded-lg border border-[#FF5500] px-6 py-3 font-body font-semibold text-[#FF5500] transition-colors hover:bg-[#FF5500]/10 disabled:opacity-60"
        }
      >
        {buying
          ? t("ig.buyNowLoading")
          : t("ig.buyNow", { price: priceRow ? priceRow.display : "" }).replace(/\s*—\s*,/, " —")}
      </button>
      {buyError && (
        <p className="mt-2 font-body text-sm text-[#FF5500]">{t("ig.buyNowError")}</p>
      )}
    </>
  );

  // Ad traffic only, and a paid landing page has no business in the index:
  // it would compete with the homepage for the same brand queries. Set from
  // the client because /ig is served by the SPA fallback (docker/nginx.conf)
  // and has no prerendered head of its own.
  useEffect(() => {
    document.title = t("ig.pageTitle");
    const robots = document.createElement("meta");
    robots.setAttribute("name", "robots");
    robots.setAttribute("content", "noindex, nofollow");
    robots.setAttribute("data-ig", "1");
    document.head.appendChild(robots);
    return () => robots.remove();
  }, [t]);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* /igbuy only. Full width, above the narrow column: there the price is
          the offer, the creative already quoted it, and the countdown is what
          makes the button urgent. On /ig and /tk it was the first thing on
          screen and it was a price, on a page whose whole job is to hand over
          a free trial — the strongest signal contradicting the ask. Renders
          nothing once the offer ends, like everywhere else it appears. */}
      {buyFirst && (
        <LaunchBanner
          endsAt={priceRow ? priceRow.launch_ends_at : null}
          originalDisplay={priceRow ? priceRow.original_display : null}
          priceDisplay={priceRow ? priceRow.display : null}
        />
      )}
      <div className="mx-auto max-w-xl px-5 pb-16 pt-2.5">
        <img src="/trapman-logo.webp" alt="TrapMan" className="h-7 w-auto" />

        {/* No "straight from Instagram" eyebrow: the visitor just came from
            Instagram and knows it. The most visible line on a paid page is
            worth a benefit, not a restatement of how they got here. */}
        <h1 className="mt-4 font-clash text-3xl font-bold leading-tight sm:text-4xl">
          {t("ig.title")}
        </h1>
        {/* The pitch splits by screen, because the ask does. On a phone the
            visitor cannot install a desktop plugin, so the email is the only
            move. On a computer they are already where the plugin runs, and
            asking them to mail themselves a link to the machine in front of
            them reads as broken.

            Split with a media query rather than the user agent: it follows a
            resized window and does not mis-file tablets. The lg breakpoint
            (1024px) rather than sm, because a phone in landscape clears sm and
            is still a phone. */}
        <p className="mt-4 font-body leading-relaxed text-[#A1A1AA] lg:hidden">
          {t("ig.lead")}
        </p>
        <p className="mt-4 hidden font-body leading-relaxed text-[#A1A1AA] lg:block">
          {t("ig.leadDesktop")}
        </p>

        {/* The price the creative quoted, as the first thing under the pitch.
            Same on both screen sizes: guest checkout works from a phone, and
            /igbuy exists precisely because that stopped being a dead end. */}
        {buyFirst && buyButton}

        {/* Desktop gets the direct path as the primary action. Rendered
            before the panel and hidden below lg, so there is only ever one
            email form on the page — mounting LeadCapturePanel twice would
            duplicate its ids and fire its pixel event twice. */}
        {!buyFirst && (
          <a
            href="/signup"
            className="mt-6 hidden rounded-lg bg-[#FF5500] px-6 py-3 text-center font-body font-semibold text-[#0a0a0a] transition-colors hover:bg-[#FF7733] lg:block"
          >
            {t("ig.trialCta")}
          </a>
        )}

        {/* Secondary on purpose: the trial is the better first step for
            someone who has never opened the plugin. But a visitor who has
            already decided should not have to pass through a signup, a
            mailbox and a login to hand over money — this goes straight to
            Stripe with no account at all. Rendered once, below, so both
            screen sizes get the same button in the same place. */}

        {/* Phone only, both branches below. Desktop gets no email path at all:
            the visitor is already at the machine the plugin runs on, so mailing
            them a link there is the offer that never made sense. `Lead` is
            phone-only as a result, which is where this campaign's traffic is
            anyway. */}

        {/* /ig shows the field itself, with nothing in front of it. It used to
            name two offers first — try it, or buy it — and open the field only
            once the visitor had chosen, on the grounds that a bare input tells
            you nothing about what it buys you. The grounds were right; the
            answer was one tap too many. The panel carries its own copy ("Leave
            your email and we'll send your free trial licence and the
            download", and a submit button that says so too), so the field is
            not bare on its own, and the tap it used to cost is gone.

            No `autoReveal` here: it scrolls the page and focuses the input,
            which is what a tap should do and what a page load must not. */}
        {!buyFirst && !isDesktop && (
          <LeadCapturePanel
            source={source}
            heading={t("ig.trialCta")}
            note={t("ig.trial")}
          />
        )}

        {/* /igbuy keeps the tap. The buy button is the primary action there and
            its ad set optimises on checkout, so an email form standing open
            above it would compete with the click that was paid for. */}
        {buyFirst && !isDesktop && panelOpen && (
          <LeadCapturePanel source={source} autoReveal />
        )}

        {/* /igbuy keeps the trial as a line of text under the price, not as a
            competing button: the creative promised it, so hiding it would be a
            bait, but the ad set optimises on checkout and two equal buttons
            split the tap. Desktop goes straight to signup — the visitor is
            already on the machine the plugin runs on — and a phone opens the
            same email panel the trial-first page uses. */}
        {buyFirst && !panelOpen && (
          <p className="mt-4 font-body text-sm text-[#A1A1AA]">
            {isDesktop ? (
              <a
                href="/signup"
                className="text-[#FF5500] underline underline-offset-2 hover:text-[#FF7733]"
              >
                {t("ig.tryFree")}
              </a>
            ) : (
              <button
                type="button"
                onClick={() => setPanelOpen(true)}
                className="text-[#FF5500] underline underline-offset-2 hover:text-[#FF7733]"
              >
                {t("ig.tryFree")}
              </button>
            )}
          </p>
        )}

        {/* Buying stays available whichever offer they leaned towards, and
            after the email field rather than before it, so opening the trial
            never buries the other door. */}
        {/* Hidden for the `nobuy` arm — the whole point of the test. /igbuy
            renders its own copy above and is never in the test. */}
        {/* Not on /ig. The `ig_no_buy` A/B test ran there from 2026-09-04 to
            2026-09-06 and the no-button arm captured 23 emails against 14 over
            331 sessions — 13.9% against 8.4%. Short of the usual bar (p=0.11),
            but the button it removes had produced 3 taps and 0 sales in the
            week before the test, so being wrong costs nothing measurable and
            the offer on this page is the trial. /tk keeps it: it was never in
            the test, and its traffic is a different audience. */}
        {!buyFirst && source !== "ig_landing" && buyButton}

        {/* Only where the panel above is not already carrying it: /ig on a
            phone shows it under the submit button instead. /igbuy keeps it
            here, under the price, because there the purchase is the primary
            action and the trial is the promise the creative made. */}
        {(buyFirst || isDesktop) && (
          <p className="mt-4 font-mono text-xs uppercase tracking-wider text-[#6a6a6a]">
            {t("ig.trial")}
          </p>
        )}

        <ul className="mt-8 space-y-3 font-body text-sm text-[#D4D4D8]">
          <li className="flex gap-3">
            <span aria-hidden="true" className="text-[#FF5500]">▪</span>
            {t("ig.point1")}
          </li>
          <li className="flex gap-3">
            <span aria-hidden="true" className="text-[#FF5500]">▪</span>
            {t("ig.point2")}
          </li>
          <li className="flex gap-3">
            <span aria-hidden="true" className="text-[#FF5500]">▪</span>
            {t("ig.point3")}
          </li>
        </ul>

        {/* The product shot was a single still until the ads started carrying
            one of their own. The hero render stays first because it is the
            frame the creative echoes, so the page opens on the image the
            visitor just tapped; the rest of the plugin follows a swipe away.
            See AD_SHOTS for why the Logic session is not among them. */}
        <div className="ig-shots mt-8">
          <ShotsCarousel surface="ad" shots={shots} />
        </div>

        {/* Mobile only: on desktop the same destination is already the
            primary button at the top, and repeating it here would compete
            with itself. Kept for the phone visitor who owns a computer and
            would rather go and open it now than wait for an email. */}
        <p className="mt-8 font-body text-sm text-[#A1A1AA] lg:hidden">
          {t("ig.desktopPrefix")}{" "}
          <a href="/signup" className="text-[#FF5500] underline underline-offset-2 hover:text-[#FF7733]">
            {t("ig.desktopCta")}
          </a>
        </p>

        {/* The one link that has to be here: this page collects an email
            address, and Meta's own ad policy expects the notice to be one tap
            away from the form. */}
        <p className="mt-6 font-mono text-xs uppercase tracking-wider text-[#6a6a6a]">
          <Link to="/privacy" className="hover:text-[#A1A1AA]">
            {t("ig.privacy")}
          </Link>
        </p>
      </div>
    </div>
  );
}
